import React, { useState, useEffect } from 'react';

const AdminDashboard = () => {
  const [flaggedEmails, setFlaggedEmails] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('confidence_asc');

  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

  useEffect(() => {
    loadDashboardData();
  }, [filter, sort]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load flagged emails
      const flaggedResponse = await fetch(
        `${API_BASE_URL}/admin/flagged-emails?status=${filter}&sort=${sort}&limit=20`, 
        {
          headers: { 'x-admin-user': 'true' }
        }
      );
      
      if (!flaggedResponse.ok) {
        throw new Error(`Failed to load flagged emails: ${flaggedResponse.statusText}`);
      }
      
      const flaggedData = await flaggedResponse.json();
      setFlaggedEmails(flaggedData.flaggedEmails || []);

      // Load analytics
      const analyticsResponse = await fetch(`${API_BASE_URL}/admin/email-patterns`, {
        headers: { 'x-admin-user': 'true' }
      });
      
      if (!analyticsResponse.ok) {
        throw new Error(`Failed to load analytics: ${analyticsResponse.statusText}`);
      }
      
      const analyticsData = await analyticsResponse.json();
      setAnalytics(analyticsData);

    } catch (error) {
      console.error('Error loading admin dashboard:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewEmail = async (positionId, action, notes = '') => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/review-email/${positionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-user': 'true'
        },
        body: JSON.stringify({ action, notes })
      });

      if (!response.ok) {
        throw new Error(`Failed to ${action} email: ${response.statusText}`);
      }

      // Reload data to reflect changes
      await loadDashboardData();
      
    } catch (error) {
      console.error(`Error ${action}ing email:`, error);
      alert(`Failed to ${action} email. Please try again.`);
    }
  };

  const getStatusBadge = (status, score) => {
    const statusConfig = {
      pending: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Pending' },
      match: { bg: 'bg-green-100', text: 'text-green-800', label: 'Match' },
      fuzzy_match: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Fuzzy Match' },
      no_match: { bg: 'bg-red-100', text: 'text-red-800', label: 'No Match' },
      manual_approved: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Approved' },
      manual_rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
          {config.label}
        </span>
        <span className="text-xs text-gray-500">
          {score !== undefined ? `${Math.round(score * 100)}%` : ''}
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Failed to load admin dashboard</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button
                onClick={loadDashboardData}
                className="mt-2 bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded-md text-sm"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
        <p className="text-gray-600">Manage work email validations and user verification</p>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow border">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Pending Review</h3>
            <p className="text-2xl font-bold text-orange-600">
              {analytics.queueStats?.pending_review || 0}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow border">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Approved</h3>
            <p className="text-2xl font-bold text-green-600">
              {analytics.queueStats?.approved || 0}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow border">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Rejected</h3>
            <p className="text-2xl font-bold text-red-600">
              {analytics.queueStats?.rejected || 0}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow border">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Total Work Emails</h3>
            <p className="text-2xl font-bold text-blue-600">
              {analytics.queueStats?.total_with_work_email || 0}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow border mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Status</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Flagged</option>
              <option value="no_match">No Match</option>
              <option value="fuzzy_match">Fuzzy Match</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort by</label>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="confidence_asc">Confidence (Low to High)</option>
              <option value="confidence_desc">Confidence (High to Low)</option>
              <option value="date_desc">Recently Updated</option>
              <option value="date_asc">Oldest First</option>
            </select>
          </div>
        </div>
      </div>

      {/* Flagged Emails Table */}
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Flagged Work Emails ({flaggedEmails.length})
          </h2>
        </div>

        {flaggedEmails.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>No flagged emails found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User & Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Work Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {flaggedEmails.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{item.user_name}</div>
                        <div className="text-sm text-gray-500">{item.position_title}</div>
                        <div className="text-sm text-gray-500">{item.company_name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{item.work_email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(item.email_domain_match_status, item.email_domain_confidence_score)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {!item.email_domain_reviewed_at && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleReviewEmail(item.id, 'approve')}
                            className="text-green-600 hover:text-green-900 bg-green-50 hover:bg-green-100 px-3 py-1 rounded-md transition-colors"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReviewEmail(item.id, 'reject')}
                            className="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-3 py-1 rounded-md transition-colors"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                      {item.email_domain_reviewed_at && (
                        <span className="text-gray-500 text-xs">
                          Reviewed {new Date(item.email_domain_reviewed_at).toLocaleDateString()}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
